/**
 * ============================================================================
 * BackgroundFx — Capa de ambiente FIJA detrás de toda la app.
 * ============================================================================
 *
 * ¿Cómo está estructurado?
 *   1. Color base sólido (bg-ink-950)            → el negro de fondo
 *   2. Rejilla de puntos                         → textura sutil
 *   3. Estela SVG curva                          → "vapor" en la parte superior
 *   4. Orbes de luz desenfocados                 → halos cyan ambiente
 *   5. Brillos puntuales (estrellas)             → micro-detalles
 *   6. Capa de ruido (granulado)                 → corta el degradado plano
 *   7. Viñeta inferior                           → asienta el contenido
 *
 * ¿Cómo lo personalizo?
 *   - Para hacer todo más / menos visible juega con la `opacity-[X]` del wrapper
 *     final o de cada capa (busca los comentarios "INTENSIDAD").
 *   - Para cambiar el color del ambiente cambia los `bg-vapor-*` (definidos en
 *     tailwind.config.js bajo la clave `vapor`).
 *   - Si quieres quitar una capa entera, comenta el bloque correspondiente.
 *
 * Notas de stacking:
 *   - Este componente se monta en App.tsx con `z-0` (dentro de un wrapper
 *     `relative isolate`). Todo el contenido va con `relative z-10` para ir
 *     por delante. NO uses `-z-10` aquí o se ocultará tras el body.
 *
 *   - `pointer-events-none` evita que esta capa intercepte clicks.
 * ============================================================================
 */
import { memo, useEffect, useMemo, useRef, useState } from 'react';

// ============================================================================
// REJILLA DE PUNTOS — parámetros
// ----------------------------------------------------------------------------
// Densidad responsiva: en móviles usamos menos puntos para aliviar CPU y DOM.
// ============================================================================
const GRID_DESKTOP = { cols: 18, rows: 10 };  // 180 dots
const GRID_MOBILE  = { cols: 10, rows: 8  };  //  80 dots

// MAX simultáneo de puntos encendidos a la vez (límite duro).
const MAX_ACTIVE = 4;
// Cada cuántos ms se intenta encender un punto nuevo.
const TICK_MS = 450;
// Duración mínima y máxima (ms) que está encendido cada punto.
const LIT_MIN_MS = 1000;
const LIT_MAX_MS = 1800;
// Duración (ms) del fundido de entrada/salida en cada destello.
const FADE_MS = 600;

// ---------------------------------------------------------------------------
// Hook: escucha media queries del navegador (mobile + prefers-reduced-motion)
// ---------------------------------------------------------------------------
function useMotionPrefs() {
  const getSnapshot = () => {
    if (typeof window === 'undefined') return { reducedMotion: false, isMobile: false };
    return {
      reducedMotion: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
      isMobile:      window.matchMedia('(max-width: 768px)').matches,
    };
  };
  const [prefs, setPrefs] = useState(getSnapshot);
  useEffect(() => {
    const mq1 = window.matchMedia('(prefers-reduced-motion: reduce)');
    const mq2 = window.matchMedia('(max-width: 768px)');
    const onChange = () => setPrefs(getSnapshot());
    mq1.addEventListener('change', onChange);
    mq2.addEventListener('change', onChange);
    return () => {
      mq1.removeEventListener('change', onChange);
      mq2.removeEventListener('change', onChange);
    };
  }, []);
  return prefs;
}

// ---------------------------------------------------------------------------
// Dot memoizado: solo se re-renderiza cuando SU prop `isOn` cambia.
// Sin este memo, cada tick (450ms) React re-renderiza todos los puntos.
// ---------------------------------------------------------------------------
const Dot = memo(function Dot({ isOn }: { isOn: boolean }) {
  return (
    <span
      className="m-auto h-1 w-1 rounded-full bg-bone-50"
      style={{
        opacity: isOn ? 1 : 0,
        transform: isOn ? 'scale(1.1)' : 'scale(0.5)',
        transition: `opacity ${FADE_MS}ms ease-in-out, transform ${FADE_MS}ms ease-in-out`,
      }}
    />
  );
});

export default function BackgroundFx() {
  const { reducedMotion, isMobile } = useMotionPrefs();
  const grid = isMobile ? GRID_MOBILE : GRID_DESKTOP;
  const totalDots = grid.cols * grid.rows;

  // ----------------------------------------------------------------------
  // Programador de destellos (pausado si el usuario pide reducir movimiento)
  // ----------------------------------------------------------------------
  const [active, setActive] = useState<Set<number>>(() => new Set());
  const timeoutsRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  useEffect(() => {
    if (reducedMotion) return;  // respeta la preferencia del usuario
    const interval = setInterval(() => {
      setActive((prev) => {
        if (prev.size >= MAX_ACTIVE) return prev;
        let pick = -1;
        for (let attempt = 0; attempt < 30; attempt++) {
          const candidate = Math.floor(Math.random() * totalDots);
          if (!prev.has(candidate)) { pick = candidate; break; }
        }
        if (pick === -1) return prev;

        const litMs = LIT_MIN_MS + Math.random() * (LIT_MAX_MS - LIT_MIN_MS);
        const t = setTimeout(() => {
          setActive((curr) => {
            const next = new Set(curr);
            next.delete(pick);
            return next;
          });
          timeoutsRef.current.delete(t);
        }, litMs);
        timeoutsRef.current.add(t);

        const next = new Set(prev);
        next.add(pick);
        return next;
      });
    }, TICK_MS);

    const timeouts = timeoutsRef.current;
    return () => {
      clearInterval(interval);
      timeouts.forEach(clearTimeout);
      timeouts.clear();
    };
  }, [totalDots, reducedMotion]);

  // Array estable: solo cambia si cambia la densidad (desktop ↔ mobile)
  const indices = useMemo(() => Array.from({ length: totalDots }, (_, i) => i), [totalDots]);

  return (
    <div aria-hidden className="pointer-events-none fixed inset-0 z-0 overflow-hidden bg-ink-950">

      {/* ---------------------------------------------------------------- */}
      {/*  1) REJILLA DE PUNTOS QUE DESTELLAN                              */}
      {/*                                                                  */}
      {/*  Una grid CSS llena de pequeños puntos. Cada punto:              */}
      {/*    - Es transparente la mayor parte del tiempo (anim twinkle)    */}
      {/*    - Pega un breve destello en su momento                        */}
      {/*    - Tiene `animationDelay` deterministico según su índice → la  */}
      {/*      sensación es de aleatoriedad sin necesidad de Math.random   */}
      {/*    - Mezcla de 3 duraciones (twinkle / -slow / -fast) para más   */}
      {/*      naturalidad                                                 */}
      {/*                                                                  */}
      {/*  Para tunear:                                                    */}
      {/*    - Más / menos densos → cambia GRID_COLS y GRID_ROWS abajo     */}
      {/*    - Más / menos visibles cuando "se encienden" → ajusta el      */}
      {/*      `bg-bone-200/40` (sube /60 para más fuerte, /20 para más    */}
      {/*      sutil)                                                      */}
      {/*    - El mask radial limita la rejilla al centro de la pantalla   */}
      {/* ---------------------------------------------------------------- */}
      <div
        className="absolute inset-0 grid"
        style={{
          gridTemplateColumns: `repeat(${grid.cols}, 1fr)`,
          gridTemplateRows: `repeat(${grid.rows}, 1fr)`,
          maskImage:
            'radial-gradient(ellipse 90% 70% at 50% 35%, #000 30%, transparent 80%)',
          WebkitMaskImage:
            'radial-gradient(ellipse 90% 70% at 50% 35%, #000 30%, transparent 80%)',
        }}
      >
        {indices.map((i) => (
          <Dot key={i} isOn={active.has(i)} />
        ))}
      </div>

      {/* ---------------------------------------------------------------- */}
      {/*  2) HALO ELÍPTICO + ONDAS DE VAPOR                               */}
      {/*                                                                  */}
      {/*  ¿Cómo funciona el movimiento?                                   */}
      {/*    - Todas las ondas usan el MISMO período horizontal (400 ud.)  */}
      {/*    - Cada <g> se desplaza con SMIL <animateTransform> de 0 a     */}
      {/*      -1200 ud. (mitad del ancho del SVG, que es 2400)            */}
      {/*    - Como 1200 = 3 períodos completos, el bucle es invisible     */}
      {/*    - Cada capa usa una duración distinta → efecto parallax       */}
      {/*    - El wrapper exterior "respira" arriba/abajo (animate-wave-   */}
      {/*      breathe), separado del centrado X para no romperlo.         */}
      {/*                                                                  */}
      {/*  Posición vertical:                                              */}
      {/*    - El wrapper usa `top-[15vh]` → escala con la altura del      */}
      {/*      viewport. Aumenta el % para bajar las ondas, redúcelo       */}
      {/*      para subirlas.                                              */}
      {/*                                                                  */}
      {/*  Para tunear:                                                    */}
      {/*    - Más / menos visible: opacity-X del SVG y stop-opacity       */}
      {/*    - Más rápido / lento:  el atributo `dur` de cada              */}
      {/*                            <animateTransform>                    */}
      {/*    - Más / menos amplitud: cambia los Q/T de los paths           */}
      {/* ---------------------------------------------------------------- */}
      {/* Wrapper: centra X y aplica la "respiración" vertical.            */}
      {/* (Separado del SVG para que la animación no machaque el centrado) */}
      {/*                                                                  */}
      {/* SOBRE EL "BUCLE INVISIBLE":                                      */}
      {/*   - SVG tiene viewBox 4800x700 y se renderiza a 4800px.          */}
      {/*   - Cada path va de x=0 a x=4800 (24 ciclos de período 400).     */}
      {/*   - El desplazamiento es -1200 (3 ciclos completos).             */}
      {/*   - Por tanto, después del desplazamiento, todavía queda path    */}
      {/*     visible desde x=-1200 hasta x=3600 → cubre 4800px de SVG     */}
      {/*     SIN HUECO en ningún momento del ciclo.                       */}
      {/*   - Al volver a 0 el patrón coincide pixel a pixel → no hay      */}
      {/*     "salto" visible al reiniciar.                                */}
      {/* ---------------------------------------------------------------- */}
      {/* MASK exterior en coordenadas del VIEWPORT.                       */}
      {/* El fade horizontal de los bordes IZQ/DER ocurre SIEMPRE en la    */}
      {/* misma posición de pantalla (no depende del path), por lo que el  */}
      {/* reinicio del bucle de animación deja de notarse.                 */}
      {/* Tunear: cambia 12% / 88% para hacer el desvanecido más / menos   */}
      {/* ancho.                                                           */}
      <div
        className="absolute inset-0 overflow-hidden"
        style={{
          maskImage:
            'linear-gradient(to right, transparent 0%, black 12%, black 88%, transparent 100%)',
          WebkitMaskImage:
            'linear-gradient(to right, transparent 0%, black 12%, black 88%, transparent 100%)',
        }}
      >
      {/* Outer: centra horizontalmente (-translate-x-1/2).                */}
      {/* Inner: aplica la animación `wave-breathe` → así el keyframe      */}
      {/* `translateY(...)` no sobrescribe el `translateX(-50%)` y el SVG  */}
      {/* se mantiene siempre centrado en pantalla.                        */}
      <div className="absolute top-[15vh] left-1/2 -translate-x-1/2 w-[4800px] max-w-none">
      <div className={reducedMotion ? '' : 'animate-wave-breathe'}>
        <svg
          className="block w-full opacity-90"
          viewBox="0 0 4800 700"
          fill="none"
          preserveAspectRatio="none"
        >
          <defs>
            {/*
              IMPORTANTE: gradientUnits="userSpaceOnUse" → el gradiente se
              ancla a las coordenadas del SVG (0..4800), NO al bounding
              box del path. Sin esto, al desplazarse el path con
              animateTransform, el fade-out del gradiente VIAJABA con él
              y entraba en pantalla creando el efecto de "perder color"
              antes del reinicio. Ahora el gradiente queda fijo y el
              path se desliza por debajo.
              Como x1=0 y x2=4800 caen en los bordes ABSOLUTOS del SVG
              (muy fuera del viewport), el path luce opacidad uniforme;
              el desvanecido visible lo aplica el mask exterior de arriba.
            */}
            <linearGradient id="vfx-line" x1="0" y1="0" x2="4800" y2="0" gradientUnits="userSpaceOnUse">
              <stop offset="0%"   stopColor="#22D3EE" stopOpacity="0.6" />
              <stop offset="100%" stopColor="#22D3EE" stopOpacity="0.6" />
            </linearGradient>

            <linearGradient id="vfx-line-warm" x1="0" y1="0" x2="4800" y2="0" gradientUnits="userSpaceOnUse">
              <stop offset="0%"   stopColor="#67E8F9" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#67E8F9" stopOpacity="0.4" />
            </linearGradient>
          </defs>

          {/*
            Se usan animaciones CSS (animate-drift-x-*) en lugar de SMIL.
            Motivo: SMIL depende del motor SMIL del navegador, que en la
            primera carga (cuando el main thread está ocupado parseando JS)
            puede demorar varios segundos en arrancar. CSS animations se
            ejecutan en el compositor (GPU) y arrancan en el primer paint.
            Si reducedMotion está activo, no se aplica la clase → estático.
          */}

          {/* ======================== CAPA 1 — drift LENTO ============= */}
          <g className={reducedMotion ? '' : 'animate-drift-x-slow'}>
            <path
              d="M 0 320 Q 100 260 200 320 T 400 320 T 600 320 T 800 320 T 1000 320 T 1200 320 T 1400 320 T 1600 320 T 1800 320 T 2000 320 T 2200 320 T 2400 320 T 2600 320 T 2800 320 T 3000 320 T 3200 320 T 3400 320 T 3600 320 T 3800 320 T 4000 320 T 4200 320 T 4400 320 T 4600 320 T 4800 320"
              stroke="url(#vfx-line)"
              strokeWidth="1.6"
              fill="none"
            />
          </g>

          {/* ======================== CAPA 2 — drift MEDIO ============== */}
          <g className={reducedMotion ? '' : 'animate-drift-x-medium'}>
            <path
              d="M 0 380 Q 100 320 200 380 T 400 380 T 600 380 T 800 380 T 1000 380 T 1200 380 T 1400 380 T 1600 380 T 1800 380 T 2000 380 T 2200 380 T 2400 380 T 2600 380 T 2800 380 T 3000 380 T 3200 380 T 3400 380 T 3600 380 T 3800 380 T 4000 380 T 4200 380 T 4400 380 T 4600 380 T 4800 380"
              stroke="url(#vfx-line-warm)"
              strokeWidth="1.2"
              fill="none"
            />
          </g>

          {/* ======================== CAPA 3 — drift RÁPIDO ============= */}
          {/* En móvil omitimos esta capa para aligerar el render SVG.     */}
          {!isMobile && (
            <g className={reducedMotion ? '' : 'animate-drift-x-fast'}>
              <path
                d="M 0 440 Q 100 400 200 440 T 400 440 T 600 440 T 800 440 T 1000 440 T 1200 440 T 1400 440 T 1600 440 T 1800 440 T 2000 440 T 2200 440 T 2400 440 T 2600 440 T 2800 440 T 3000 440 T 3200 440 T 3400 440 T 3600 440 T 3800 440 T 4000 440 T 4200 440 T 4400 440 T 4600 440 T 4800 440"
                stroke="url(#vfx-line)"
                strokeWidth="0.9"
                fill="none"
                opacity="0.65"
              />
            </g>
          )}

          {/* ======================== CAPA 4 — sentido INVERSO ========== */}
          {/* Reutiliza la animación `drift-x-slow` con direction:reverse   */}
          {/* y un duration distinto para dar parallax.                    */}
          {!isMobile && (
            <g
              className={reducedMotion ? '' : 'animate-drift-x-slow'}
              style={reducedMotion ? undefined : { animationDirection: 'reverse', animationDuration: '36s' }}
            >
              <path
                d="M 0 500 Q 100 450 200 500 T 400 500 T 600 500 T 800 500 T 1000 500 T 1200 500 T 1400 500 T 1600 500 T 1800 500 T 2000 500 T 2200 500 T 2400 500 T 2600 500 T 2800 500 T 3000 500 T 3200 500 T 3400 500 T 3600 500 T 3800 500 T 4000 500 T 4200 500 T 4400 500 T 4600 500 T 4800 500"
                stroke="url(#vfx-line)"
                strokeWidth="0.8"
                fill="none"
                opacity="0.5"
              />
            </g>
          )}
        </svg>
      </div>
      </div>
      </div>

      {/* ---------------------------------------------------------------- */}
      {/*  3) FOCOS / HALOS CYAN  (INTENSIDAD: bg-vapor-X/[0.XX])          */}
      {/*  Dos focos en posiciones distintas para dar profundidad ambiente */}
      {/*  sin estorbar a las ondas (que llevan su propio acento cyan).    */}
      {/*    - PRINCIPAL: arriba-izquierda, fuerte (≈18% opacidad)         */}
      {/*    - SECUNDARIO: abajo-derecha, sutil (≈8% opacidad)             */}
      {/*  Para quitar uno, comenta su <div>. Para hacerlos más / menos    */}
      {/*  visibles, sube/baja la fracción /[0.XX].                        */}
      {/* ---------------------------------------------------------------- */}
      <div
        className="absolute top-[6%] -left-[10%] h-[640px] w-[640px] rounded-full bg-vapor-500/[0.18] blur-3xl"
      />
      <div
        className="absolute bottom-[10%] -right-[8%] h-[460px] w-[460px] rounded-full bg-vapor-400/[0.08] blur-3xl"
      />

      {/* ---------------------------------------------------------------- */}
      {/*  5) RUIDO FINO  (INTENSIDAD: opacity-[0.X])                      */}
      {/*  Granulado SVG definido en tailwind.config.js (clase bg-noise).  */}
      {/*  Le da sensación de "papel" y rompe los degradados planos.       */}
      {/* ---------------------------------------------------------------- */}
      <div className="absolute inset-0 bg-noise opacity-[0.4] mix-blend-overlay" />

      {/* ---------------------------------------------------------------- */}
      {/*  6) VIÑETA INFERIOR                                              */}
      {/*  Fade negro en la parte de abajo para que el footer "asiente".   */}
      {/*  Quita la línea entera si no quieres viñeta.                     */}
      {/* ---------------------------------------------------------------- */}
      <div className="absolute inset-x-0 bottom-0 h-64 bg-gradient-to-t from-ink-950 to-transparent" />
    </div>
  );
}
